'use client'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight, Check, Sparkles, FileText } from 'lucide-react'
import { useAuth } from '@/lib/auth/context'
import { useAuthModal } from '@/components/auth/auth-modal-context'
import { SoftExportBar, ExportModal } from '@/components/calc'
import type { CartaoPayload, CartaoLinha } from '@/lib/export/types'

const CATEGORIAS = [
  'Material e insumos',
  'Ferramentas e apps',
  'Marketing e vendas',
  'Transporte e frete',
  'Taxas (maquininha, banco)',
  'DAS e outros impostos',
  'Outras despesas',
]

function parseNum(s: string): number {
  const n = parseFloat(s.replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, ''))
  return Number.isNaN(n) ? 0 : n
}

const fmt = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export function DreCalc() {
  const { user } = useAuth()
  const { openSignup } = useAuthModal()
  const isPremium = user?.plano === 'PREMIUM' || user?.role === 'ADMIN'
  const [receita, setReceita] = useState('')
  const [despesas, setDespesas] = useState<string[]>(() => Array(CATEGORIAS.length).fill(''))
  const [exportOpen, setExportOpen] = useState(false)

  const R = parseNum(receita)
  const linhas = CATEGORIAS.map((nome, i) => ({ nome, valor: parseNum(despesas[i]) }))
  const totalDespesas = linhas.reduce((s, l) => s + l.valor, 0)
  const resultado = R - totalDespesas
  const margem = R > 0 ? (resultado / R) * 100 : 0
  const temResultado = R > 0

  /** Cartão do DRE pro export (usa a mesma cota/marca/paywall das calculadoras). */
  const buildCartao = (): CartaoPayload => {
    const itens: CartaoLinha[] = [
      { label: 'Receita bruta', value: fmt(R) },
      ...linhas
        .filter((l) => l.valor > 0)
        .map((l) => ({ label: l.nome, value: `− ${fmt(l.valor)}`, tone: 'muted' as const, sub: true })),
      { label: 'Total de despesas', value: `− ${fmt(totalDespesas)}`, strong: true },
      { label: 'Margem', value: `${margem.toFixed(1).replace('.', ',')}%`, tone: 'muted' as const },
    ]
    return {
      heroLabel: resultado >= 0 ? 'Lucro do período' : 'Prejuízo do período',
      heroValue: fmt(resultado),
      tone: resultado >= 0 ? 'brand' : 'neg',
      linhas: itens,
      nome: 'DRE simplificado',
    }
  }

  function setDespesa(i: number, v: string) {
    setDespesas((prev) => {
      const next = [...prev]
      next[i] = v
      return next
    })
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Entradas */}
      <div className="border border-line bg-surface p-5 sm:p-6">
        <label className="text-[13px] font-semibold text-ink-mute">Receita do período (faturamento)</label>
        <div className="mt-1 flex items-center gap-2 rounded-lg border border-line bg-surface px-3 focus-within:border-brand">
          <span className="text-[13px] text-ink-faint">R$</span>
          <input
            inputMode="decimal"
            value={receita}
            onChange={(e) => setReceita(e.target.value)}
            placeholder="0,00"
            className="h-11 w-full bg-transparent font-mono text-[15px] text-ink outline-none"
          />
        </div>

        <p className="mt-5 text-[13px] font-semibold text-ink-mute">Despesas por categoria</p>
        <div className="mt-2 space-y-2">
          {CATEGORIAS.map((nome, i) => (
            <div key={nome} className="flex items-center gap-2 rounded-lg border border-line bg-white px-3 py-1.5">
              <span className="flex-1 text-[13px] text-ink-mute">{nome}</span>
              <span className="text-[12px] text-ink-faint">R$</span>
              <input
                inputMode="decimal"
                value={despesas[i]}
                onChange={(e) => setDespesa(i, e.target.value)}
                placeholder="0,00"
                className="w-24 bg-transparent text-right font-mono text-[13px] text-ink outline-none"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Demonstrativo */}
      <div className="flex flex-col border border-line bg-surface p-5 sm:p-6">
        <p className="text-[12px] font-bold uppercase tracking-wide text-ink-faint">Demonstrativo de resultado</p>

        <div className="mt-3 space-y-1.5 text-[13.5px]">
          <div className="flex items-center justify-between font-semibold text-ink">
            <span>Receita bruta</span>
            <span className="font-mono">{fmt(R)}</span>
          </div>
          <div className="pt-1 text-[12px] font-semibold uppercase tracking-wide text-ink-faint">(−) Despesas</div>
          {linhas.filter((l) => l.valor > 0).length === 0 ? (
            <div className="py-1 text-[12.5px] text-ink-faint">Nenhuma despesa informada.</div>
          ) : (
            linhas
              .filter((l) => l.valor > 0)
              .map((l) => (
                <div key={l.nome} className="flex items-center justify-between text-ink-mute">
                  <span className="pl-2">{l.nome}</span>
                  <span className="font-mono">− {fmt(l.valor)}</span>
                </div>
              ))
          )}
          <div className="flex items-center justify-between border-t border-line pt-2 font-semibold text-ink">
            <span>(=) Total de despesas</span>
            <span className="font-mono">− {fmt(totalDespesas)}</span>
          </div>
        </div>

        <div className="mt-4 border-t-2 border-ink pt-3">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] font-bold text-ink">
              {resultado >= 0 ? '(=) Lucro do período' : '(=) Prejuízo do período'}
            </span>
            <span
              className={`font-mono text-[22px] font-bold ${resultado >= 0 ? 'text-brand' : 'text-[rgb(var(--coral))]'}`}
            >
              {temResultado ? fmt(resultado) : 'R$ —'}
            </span>
          </div>
          {temResultado && (
            <p className="mt-1 text-[12.5px] text-ink-mute">
              Margem de {margem.toFixed(1).replace('.', ',')}% do faturamento
            </p>
          )}
        </div>

        {/* Levar o DRE embora: entra na cota de export (limpo só no Premium) */}
        {temResultado && (
          <>
            <SoftExportBar onExport={() => setExportOpen(true)} />
            <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} buildCartao={buildCartao} />
          </>
        )}

        <div className="mt-auto pt-6">
          <div className="rounded-xl border-2 border-brand bg-brand-tint px-5 py-4">
            <p className="flex items-start gap-2 text-[14px] font-bold leading-snug text-ink">
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              Seu DRE, atualizado sozinho — e pronto pro contador.
            </p>
            <ul className="mt-3 space-y-1.5">
              {[
                { t: 'Este DRE é grátis e sem limite — calcule quantas vezes quiser', premium: false },
                { t: 'Lance mês a mês e o DRE se refaz sozinho', premium: true },
                { t: 'Relatório pro contador (PDF limpo, mensal e anual)', premium: true },
              ].map((b) => (
                <li key={b.t} className="flex items-start gap-2 text-[12.5px] text-ink-soft">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
                  <span>
                    {b.t}
                    {b.premium && <span className="ml-1 font-bold text-brand">· Premium</span>}
                  </span>
                </li>
              ))}
            </ul>
            {isPremium ? (
              <Link
                href="/dashboard"
                className="mt-3.5 inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-[13px] font-bold text-white hover:bg-brand-dark"
              >
                Abrir meu painel <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : user ? (
              <Link
                href="/premium"
                className="mt-3.5 inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-[13px] font-bold text-white hover:bg-brand-dark"
              >
                <Sparkles className="h-3.5 w-3.5" /> Relatório pro contador — Premium
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => openSignup('dre')}
                className="mt-3.5 inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-[13px] font-bold text-white hover:bg-brand-dark"
              >
                Criar conta grátis e controlar meu MEI <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
